import React, { Suspense, lazy } from 'react';
import { CrmConfig, formatNameUrl, formatPhoneUrl, formatDisplayHtml } from './crmUtils';

const ChatClient = lazy(() => import('../ChatClient').then(m => ({ default: m.ChatClient })));

interface CrmChatTabProps {
  activeTab: 'logs' | 'chat' | 'cdr' | 'voicemails';
  isLight: boolean;
  crm?: CrmConfig;
  updateCrmSettings: (payload: any, adminPassword?: string) => Promise<void>;
  chatDids: { number: string; didSummary: string }[];
  defaultChatDid?: string;
  defaultDid?: string;
  chatFolders: string[];
  fetchConversations: (offset?: number, limit?: number, did?: string, folder?: string) => Promise<any>;
  fetchUnreadCounts: () => Promise<any>;
  fetchThreadMessages: (contact: string, offset?: number, limit?: number, did?: string) => Promise<any>;
  fetchSearchResults: (query: string, limit?: number) => Promise<any>;
  fetchExportMessages: (contact: string, since?: number, did?: string) => Promise<any>;
  sendChatMessage: (payload: any) => Promise<any>;
  deleteConversations: (did: string, contacts: string[], rightsPassword?: string) => Promise<any>;
  moveConversations: (did: string, contacts: string[], folder: string, rightsPassword?: string) => Promise<any>;
  manageCustomFolder: (action: 'add' | 'remove', folder: string) => Promise<any>;
  sendChatEvent: (payload: { did: string; contact: string; type: 'FLAG' | 'PIN' | 'PRINT'; value: string; agentName: string }, rightsPassword?: string) => Promise<any>;
  resolveCnamMap: (numbers: string[]) => Promise<Record<string, string>>;
  uploadChatMedia: (formData: FormData) => Promise<string[]>;
  markMessagesAsRead: (messageIds: string[]) => Promise<void>;
  markMessagesAsUnread: (messageIds: string[]) => Promise<void>;
  slug?: string;
  authName: string;
  authInput: string;
  setUnreadMessages: (count: number) => void;
  chatTrigger: { contact: string; did?: string; ts: number; autoLookup?: boolean } | null;
  cnamRefreshTrigger?: number;
  cnamVersion?: number;
  onEditContact: (phone: string, name: string) => void;
}

export function CrmChatTab({
  activeTab,
  isLight,
  crm,
  updateCrmSettings,
  chatDids,
  defaultChatDid,
  defaultDid,
  chatFolders,
  fetchConversations,
  fetchUnreadCounts,
  fetchThreadMessages,
  fetchSearchResults,
  fetchExportMessages,
  sendChatMessage,
  deleteConversations,
  moveConversations,
  manageCustomFolder,
  sendChatEvent,
  resolveCnamMap,
  uploadChatMedia,
  markMessagesAsRead,
  markMessagesAsUnread,
  slug,
  authName,
  authInput,
  setUnreadMessages,
  chatTrigger,
  cnamRefreshTrigger,
  cnamVersion,
  onEditContact
}: CrmChatTabProps) {
  if (!crm?.enableChatClient) return null;

  return (
    <div
      style={{
        display: activeTab === 'chat' ? 'flex' : 'none',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        width: '100%',
        background: isLight ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.02)',
        border: isLight ? '1px solid rgba(0,0,0,0.1)' : '1px solid rgba(255,255,255,0.05)',
        borderRadius: '12px',
        overflow: 'hidden'
      }}
    >
      <Suspense fallback={<div style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-muted)' }}>Loading Messaging Interface...</div>}>
        <ChatClient
          crmConfig={crm}
          updateCrmSettings={updateCrmSettings}
          dids={chatDids}
          defaultDid={defaultDid || defaultChatDid || ''}
          chatFolders={chatFolders}
          fetchConversations={fetchConversations}
          fetchUnreadCounts={fetchUnreadCounts}
          fetchThreadMessages={fetchThreadMessages}
          fetchSearchResults={fetchSearchResults}
          fetchExportMessages={fetchExportMessages}
          sendMessage={sendChatMessage}
          deleteConversations={deleteConversations}
          moveConversations={moveConversations}
          manageCustomFolder={manageCustomFolder}
          sendEvent={sendChatEvent}
          resolveCnamMap={resolveCnamMap}
          uploadMedia={uploadChatMedia}
          markAsRead={markMessagesAsRead}
          markAsUnread={markMessagesAsUnread}
          isLight={isLight}
          slug={slug}
          agentName={authName || (slug && localStorage.getItem(`crm-agent-${slug}`)) || ''}
          crmPassword={authInput || (slug && localStorage.getItem(`crm-pw-${slug}`)) || ''}
          hasPinFlagPassword={!!crm?.pinFlagPasswordHash}
          hasPrintPassword={!!crm?.printPasswordHash}
          hasAdminPassword={!!crm?.adminPasswordHash}
          onUnreadCountChange={setUnreadMessages}
          renderContactTitle={(contact, cnam, disableLinks, disableEditIcon, hidePhoneIfName) => {
            const urlName = formatNameUrl(cnam, crm?.nameUrlFormat || '');
            const urlPhone = formatPhoneUrl(contact, crm?.phoneUrlFormat || '');
            return formatDisplayHtml(cnam, contact, crm, urlName, urlPhone, 0, isLight, null, disableLinks, disableEditIcon, hidePhoneIfName, onEditContact);
          }}
          chatTrigger={chatTrigger}
          cnamVersion={cnamVersion ?? cnamRefreshTrigger ?? 0}
        />
      </Suspense>
    </div>
  );
}
